import { Box, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography, Paper } from "@mui/material";
import React, { FC, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { usePasswordResetMutation } from "../../redux/reducers/settings.reducer";
import { LoadingButton } from "@mui/lab";
import { useAppDispatch } from "../../redux";
import { showFeedbackNotification } from "../../redux/reducers/feedbackNotification.reducer";

interface IForgotPasswordDialogProps {
	open: boolean;
	onClose: () => void;
}

interface IForgotPasswordInputs {
	username: string;
}

const ForgotPasswordDialog: FC<IForgotPasswordDialogProps> = (props) => {
	const { open, onClose } = props;

	const dispatch = useAppDispatch();

	const {
		reset,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<IForgotPasswordInputs>();

	// API
	// PASSWORD RESET
	const [
		passwordReset,
		{
			isLoading: passwordResetLoading,
			error: passwordResetError,
			data: passwordResetResponse,
			reset: resetPasswordResetState
		}
	] = usePasswordResetMutation();

	function handleSubmitForm(formData: IForgotPasswordInputs) {
		passwordReset(formData.username);
	}

	useEffect(() => {
		if (open) {
			reset({ username: "" });
		} else {
			resetPasswordResetState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => {
		if (passwordResetResponse) {
			onClose();
			dispatch(
				showFeedbackNotification({
					message: "Password reset link has been sent to your email.",
					severity: "success"
				})
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [passwordResetResponse]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle textAlign="center" classes={{ root: "dialog-title-root primary" }}>
				Reset Your Password
				<Box className="close-dialog-icon-wrapper">
					<IconButton onClick={onClose} size="small" color="secondary">
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent classes={{ root: "dialog-content-root" }}>
				<Paper elevation={0} classes={{ root: "forgot-password-info-wrapper" }}>
					<Typography fontWeight={500} color="text.primary" textAlign="center">
						Enter your username below and we'll send you a password reset link via email.
					</Typography>
				</Paper>

				<Box component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
					<Box sx={{ mb: "1rem" }}>
						<Typography fontWeight={600} color="text.primary" marginBottom="0.5rem">
							Username
						</Typography>

						<TextField
							required
							fullWidth
							autoFocus
							size="small"
							autoComplete="off"
							variant="outlined"
							placeholder="Enter your username"
							error={!!errors.username}
							helperText={errors.username && errors.username.message ? errors.username.message.toString() : ""}
							{...register("username", { required: "Username is required" })}
						/>
					</Box>

					{passwordResetError ? (
						<Typography variant="body1" color="error" textAlign="center" marginBottom="1rem">
							{"data" in passwordResetError && passwordResetError.data
								? String(passwordResetError.data)
								: "Something went wrong"}
						</Typography>
					) : null}

					<Box className="content-center">
						<LoadingButton
							disableElevation
							variant="contained"
							color="primary"
							type="submit"
							loading={passwordResetLoading}
						>
							Send Reset Link
						</LoadingButton>
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default ForgotPasswordDialog;
